import React, { useState } from 'react';
import { Box, Container } from '@mui/material';
import {
  SectionTitle,
  RentalsCard,
  Carousel,
} from 'views/rebranded-module/common/components';
import StyledBox from 'views/rebranded-module/location-module/LocationViewV2/components/RentalsSection/RentalsSection.styles';
import { interpolateString } from 'views/rebranded-module/common/utils/interpolateString';
import PATHS from 'lib/navigation/paths';
import moneyFormat from 'views/rebranded-module/common/utils/moneyFormat';
import Dinero from 'views/rebranded-module/common/utils/Dinero';
import { ThemeProvider } from '@mui/material/styles';
import retouchTheme from 'lib/config/retouch-rebranding-theme';
import dynamic from 'next/dynamic';

const MainWidgetDialog = dynamic(() => import('views/common/components/UI/MainWidgetDialog').then(
  (file) => file.MainWidgetDialog,
));

// function to find the lowest rate
const getLowestRate = (rateRanges) => {
  if (!rateRanges || rateRanges.length === 0) return null;

  // Find the rate with the minimum amount
  return rateRanges.reduce((min, current) => (current.rate.amount < min.rate.amount ? current : min)).rate;
};

const RentalsSection = (props) => {
  const { content, data } = props;
  const [WidgetDialogData, setOpenWidgetDialogData] = useState({
    open: false,
    vmId: null,
    vmName: null,
    vmImage: null,
  });
  const btnAction = {
    text: interpolateString(content.LocationPage.motorcycleRentalsAction, {
      location: data.location.display_name,
    }),
    path: PATHS.motorcycleRentalsSlug(data.location.slug),
  };
  const handleWidgetDialogOpen = (vmData) => {
    setOpenWidgetDialogData((prevData) => ({
      ...prevData,
      open: true,
      vmId: vmData.id,
      vmName: vmData.name,
      vmImage: {
        src: vmData.image || 'imgs/Rebranding/logos/default.jpg',
        alt: vmData.name, // Use alt_text if available, fallback to name
      },
    }));
  };
  const handleWidgetDialogClose = () => {
    setOpenWidgetDialogData((prevData) => ({
      ...prevData,
      open: false,
      vmId: null,
      vmName: null,
      vmImage: null,
    }));
  };
  const renderRentalCard = (rental) => {
    const lowestRate = getLowestRate(rental.rate_ranges);
    const formattedPrice = lowestRate
      ? moneyFormat(
        Dinero({
          amount: lowestRate.amount,
          currency: lowestRate.currency_code,
          precision: lowestRate.precision,
        }),
      )
      : ' ';

    return (
      <RentalsCard
        handleCardClick={handleWidgetDialogOpen}
        key={rental.id}
        id={rental.id}
        image={rental.image}
        imageAlt={rental.alt_text}
        title={rental.display_name}
        name={rental.name_without_make}
        make={rental.vehicle_make.name}
        content={content.RentalsCardComponent}
        startPrice={formattedPrice}
        specsPath={PATHS.vehicleModel.index(
          data.location.slug,
          rental.vehicle_make.slug,
          rental.slug,
        )}
      />
    );
  };

  return (
    <StyledBox>
      <Container className="LVCustomContainer">
        <Box className="TSContent">
          <SectionTitle
            title={content.LocationPage.motorcycleRentalsTitle}
            text={interpolateString(
              content.LocationPage.motorcycleRentalsSubtitle,
              {
                count: data.vehicle_models_component.motorcycles.length,
                location: data.location.display_name,
              },
            )}
            action={btnAction}
          />
          <Box className="IRSSectionContainer" mt={4}>
            {/* Desktop view */}
            <Box position="relative">
              <Carousel
                arrowOutside
                swipeable
                infinite
                showDots={false}
                keyBoardControl
                minimumTouchDrag={20}
                renderDotsOutside
                deskTopItems={3}
                carouselArrow
                customTransition="transform 200ms ease-in">
                {data.vehicle_models_component.motorcycles.map(
                  renderRentalCard,
                )}
              </Carousel>
            </Box>
          </Box>
        </Box>
      </Container>
      {WidgetDialogData.open && (
        <ThemeProvider theme={retouchTheme}>
          <MainWidgetDialog
            open={WidgetDialogData.open}
            handleClose={handleWidgetDialogClose}
            reservationData={{
              pickup_location_id: data.location.id,
              dropoff_location_id: data.location.id,
              vehicle_model_id: WidgetDialogData.vmId,
            }}
            floatingDialog
            renderFullWidgetByForce
            fixPosition
            bikeName={WidgetDialogData.vmName}
            image={{
              src: WidgetDialogData.vmImage.src,
              alt: WidgetDialogData.vmImage.alt,
            }}
          />
        </ThemeProvider>
      )}
    </StyledBox>
  );
};

export { RentalsSection };
